"use client";

import { StreakSummaryResponseV2 } from "@cede/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertTriangle, X } from "lucide-react";

interface CancelledStreakNotificationItemProps {
  streak: StreakSummaryResponseV2;
  onDismiss: (streakId: string) => void;
  onClick: (streak: StreakSummaryResponseV2) => void;
}

export const CancelledStreakNotificationItem = ({
  streak,
  onDismiss,
  onClick,
}: CancelledStreakNotificationItemProps) => {
  return (
    <div
      className={cn(
        "relative flex items-start gap-3 p-3",
        "rounded-lg",
        "bg-cede-error/5 hover:bg-cede-error/10",
        "transition-colors duration-200",
        "cursor-pointer",
        "group",
      )}
      onClick={() => onClick(streak)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(streak);
        }
      }}
    >
      {/* Icon */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cede-error/10">
        <AlertTriangle className="h-5 w-5 text-cede-error" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2 pr-6">
          <div>
            <p className="caption font-medium uppercase tracking-wide text-cede-error">Autopilot Cancelled</p>
            <p className="body2 text-cede-text-primary">
              <span className="font-medium">{streak.campaignName}</span> — Epoch {streak.epochId + 1}
            </p>
          </div>
        </div>
        <p className="caption mt-1 text-cede-text-secondary">
          Your autopilot was stopped because the epoch configuration was updated. Please set up a new one.
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-cede-text-secondary">
          <span className="inline-flex items-center rounded-full border border-cede-border-container bg-cede-bg-component/50 px-2 py-0.5">
            {streak.pairSymbol}
          </span>
        </div>
      </div>

      {/* Dismiss button */}
      <Button
        variant="transparent"
        size="small"
        className={cn("absolute right-2 top-2", "h-6 w-6 min-w-0 p-0", "opacity-100")}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(streak.id);
        }}
        aria-label={`Dismiss notification for cancelled streak on ${streak.campaignName}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
